import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, FilePdf, FileDoc, Signature, Trash, Check, X } from "@phosphor-icons/react";
import PdfSignPlacer from "@/components/PdfSignPlacer";

const fmtSize = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [signModal, setSignModal] = useState(null); // {doc, note, busy, positions, blob}
  const [hasSignature, setHasSignature] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/documents"); setDocs(data || []); }
    catch (e) { toast.error("Failed to load documents"); }
  };

  const checkSig = async () => {
    try { const { data } = await api.get("/users/me"); setHasSignature(!!data.signature_data_url); }
    catch (e) { /* silent */ }
  };

  useEffect(() => { load(); checkSig(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"? This can't be undone.`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const download = async (d, signed = false) => {
    try {
      const token = localStorage.getItem("sakshi_token");
      const res = await fetch(`${API}/documents/${d.id}${signed ? "?signed=true" : ""}`, { headers: { Authorization: `Bearer ${token}` }});
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Download failed"); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stem = d.filename.replace(/\.[^.]+$/, "");
      const ext = d.filename.split(".").pop();
      a.download = signed ? `${stem}_signed.${ext}` : d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(String(e.message || e)); }
  };

  const confirmSign = async () => {
    if (!signModal) return;
    setSignModal({ ...signModal, busy: true });
    try {
      const payload = { note: signModal.note || "" };
      if (signModal.doc.kind === "pdf" && (signModal.positions || []).length > 0) {
        payload.positions = signModal.positions;
      }
      await api.post(`/documents/${signModal.doc.id}/sign`, payload);
      toast.success(`Signed "${signModal.doc.filename}"`);
      setSignModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
      setSignModal({ ...signModal, busy: false });
    }
  };

  const openSignModal = async (d) => {
    const initial = { doc: d, note: "", positions: [], blob: null };
    setSignModal(initial);
    if (d.kind === "pdf") {
      try {
        const token = localStorage.getItem("sakshi_token");
        const res = await fetch(`${API}/documents/${d.id}`, { headers: { Authorization: `Bearer ${token}` }});
        const blob = await res.blob();
        setSignModal({ ...initial, blob });
      } catch (e) { /* fallback to default position */ }
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="documents-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Digital signing</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Documents</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">Upload any PDF or DOCX and digitally stamp it with your signature. Kept separate from Sales Suite-generated proposals.</p>
        </div>
        <label className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-semibold rounded-sm transition-colors duration-200 cursor-pointer disabled:opacity-60" data-testid="doc-upload-label">
          <UploadSimple size={16} weight="bold"/>
          {busy ? "Uploading…" : "Upload PDF / DOCX"}
          <input ref={fileRef} type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                 onChange={upload} disabled={busy} className="hidden" data-testid="doc-upload-input"/>
        </label>
      </div>

      {!hasSignature && (
        <div className="mt-4 border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3 rounded-sm" data-testid="no-sig-warning">
          You don't have a saved signature yet. Head to <a href="/profile" className="underline font-semibold">Profile</a> to add one before you can sign documents.
        </div>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="doc-list">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-2 label-micro">File</th>
              <th className="px-4 py-2 label-micro">Size</th>
              <th className="px-4 py-2 label-micro">Signatures</th>
              <th className="px-4 py-2 label-micro">Uploaded</th>
              <th className="px-4 py-2 label-micro w-64">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400 text-sm">No documents yet — upload a PDF or DOCX to start.</td></tr>
            )}
            {docs.map(d => (
              <tr key={d.id} className="border-b border-slate-100" data-testid={`doc-row-${d.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {d.kind === "pdf"
                      ? <FilePdf size={18} className="text-red-600 shrink-0"/>
                      : <FileDoc size={18} className="text-blue-600 shrink-0"/>}
                    <div className="font-semibold text-slate-900 truncate">{d.filename}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{d.uploaded_by_name}</div>
                </td>
                <td className="px-4 py-3 tabular-nums text-slate-600">{fmtSize(d.size_bytes)}</td>
                <td className="px-4 py-3">
                  {(d.signatures || []).length === 0
                    ? <span className="text-xs text-slate-500">—</span>
                    : (
                      <div className="flex flex-col gap-0.5">
                        {(d.signatures || []).map((s, i) => (
                          <div key={i} className="text-[11px]"><Check size={10} weight="bold" className="inline text-emerald-600 mr-1"/><b>{s.name}</b> · {new Date(s.signed_at).toLocaleDateString()}</div>
                        ))}
                      </div>
                    )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{new Date(d.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => openSignModal(d)} disabled={!hasSignature} data-testid={`doc-sign-${d.id}`}
                            className="text-xs inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-2.5 py-1.5 disabled:opacity-50 transition-colors">
                      <Signature size={12}/> Sign
                    </button>
                    <button onClick={() => download(d, false)} data-testid={`doc-dl-${d.id}`} className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1.5 hover:bg-slate-100">
                      <DownloadSimple size={12}/> Original
                    </button>
                    {d.is_signed && (
                      <button onClick={() => download(d, true)} data-testid={`doc-dl-signed-${d.id}`} className="text-xs inline-flex items-center gap-1 border border-emerald-500 text-emerald-700 rounded-sm px-2.5 py-1.5 hover:bg-emerald-50">
                        <DownloadSimple size={12}/> Signed
                      </button>
                    )}
                    <button onClick={() => del(d)} data-testid={`doc-del-${d.id}`} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2.5 py-1.5 hover:bg-red-50">
                      <Trash size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {signModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="sign-modal" onClick={() => !signModal.busy && setSignModal(null)}>
          <div className="bg-white w-full max-w-4xl border border-slate-200 rounded-sm shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="label-micro">Sign document</div>
                <div className="font-display font-bold text-lg mt-0.5 truncate">{signModal.doc.filename}</div>
              </div>
              <button onClick={() => setSignModal(null)} disabled={signModal.busy} data-testid="sign-modal-close" className="p-1 hover:bg-slate-100 rounded-sm"><X size={16}/></button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm overflow-y-auto">
              {signModal.doc.kind === "pdf" ? (
                signModal.blob ? (
                  <PdfSignPlacer fileBlob={signModal.blob}
                                 positions={signModal.positions || []}
                                 onChange={(next) => setSignModal({ ...signModal, positions: next })}/>
                ) : (
                  <div className="text-xs text-slate-500">Loading PDF preview…</div>
                )
              ) : (
                <p className="text-slate-700">DOCX files are signed with a signature block appended at the end (click-to-place isn't supported for DOCX).</p>
              )}
              <label className="block">
                <span className="label-micro">Optional note</span>
                <textarea rows={2} value={signModal.note} onChange={(e) => setSignModal({ ...signModal, note: e.target.value })}
                          placeholder="e.g. Reviewed & approved for release" data-testid="sign-modal-note"
                          className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"/>
              </label>
              {signModal.doc.kind === "pdf" && (signModal.positions || []).length === 0 && (
                <div className="text-[11px] text-slate-500">No positions chosen — signature will default to bottom-right of the last page.</div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={() => setSignModal(null)} disabled={signModal.busy} data-testid="sign-modal-cancel" className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
              <button onClick={confirmSign} disabled={signModal.busy} data-testid="sign-modal-confirm"
                      className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60 transition-colors">
                <Signature size={14}/> {signModal.busy ? "Signing…" : `Sign now${signModal.doc.kind === "pdf" && (signModal.positions||[]).length > 0 ? ` (${signModal.positions.length})` : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
